import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Button, DatePicker, Form, Input, message, TimePicker } from "antd";
import dayjs from "dayjs";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserContext } from "../../../amplify/authenticator-provider";
import { CREATE_SCHEDULED_MESSAGE, UPDATE_SCHEDULED_MESSAGE } from "../../../graphql/mutation";
import { GET_COMPANY_WORK_HOURS } from "../../../graphql/query";
import { timezones } from "../../../library/constants";
import { addScheduledMessage, updateScheduledMessageRedux } from "../../../redux/slices/leadSlice";
import { convertUtcToLocal } from "../../../library/utils";
import { resolveLeadOpportunityId } from "../../../helpers/opportunityHelper";
import {
  DATE_TIME_FORMATS,
  formatDate,
  getScheduleTimezone,
  getWorkingHoursDisabledDate,
  getWorkingHoursDisabledTime,
  viewerZoneEquivalent,
} from "../../../utils/dateTimes";

const utc = require("dayjs/plugin/utc");

dayjs.extend(utc);

const ScheduleMessagesPopup = ({
  scheduledMessages,
  getMessages,
  messageData: editMessageData,
  selectedLead,
  mode = "EDIT",
  // Active Sales / Service / Parts tab, drilled from the composer so a
  // scheduled message targets the same opportunity an immediate send would.
  selectedOppTypeId = null,
  setmessageText = () => {},
  reset = () => {},
  close = () => {},
}) => {
  const [form] = Form.useForm();
  const { user } = useContext(UserContext);
  const dispatch = useDispatch();
  const { messageOpportunity } = useSelector(state => state.lead);
  const client = useApolloClient();

  // The scheduled-messages list is queried in engagementActivity and mirrored
  // into redux. Refresh it by operation name after a change so the drawer shows
  // the SERVER's rows, not just the optimistic one pushed below — the same
  // thing the scheduled-EMAIL paths do with their own refetch.
  const refetchScheduledMessagesList = () =>
    client
      .refetchQueries({ include: ["getScheduledMessagesForLead"] })
      .catch(err => console.error("scheduled messages refetch failed", err));

  // The location the schedule is expressed in — shared with the email scheduler.
  const timeZone = useMemo(
    () => getScheduleTimezone(user, selectedLead?.location),
    [user, selectedLead?.location]
  );

  const initialState = {
    message: editMessageData?.message,
    date:
      mode === "EDIT" ? dayjs(editMessageData?.taskSendDatetime).tz(timeZone).toISOString() : null,
    timezone: timeZone,
  };
  const [workingHours, setworkingHours] = useState([]);
  const [createScheduledMessage, { loading: createLoading }] =
    useMutation(CREATE_SCHEDULED_MESSAGE);
  const [updateScheduledMessage, { loading: updateLoading }] =
    useMutation(UPDATE_SCHEDULED_MESSAGE);
  useQuery(GET_COMPANY_WORK_HOURS, {
    variables: {
      companyId: user.company.id,
      locationId: (sessionStorage.getItem("currentLocationId") || "").includes(",")
        ? selectedLead?.location?.id
        : sessionStorage.getItem("currentLocationId"),
    },
    onCompleted: res => {
      setworkingHours(res.getCompanyWorkingHours);
    },
  });
  const [messageData, setmessageData] = useState(initialState);

  // useEffect(() => {
  //   const obj = timezones.find(item => item.value === dayjs.tz.guess());
  //   console.log(obj);
  //   if (obj) setmessageData({ ...messageData, timezone: obj.label });
  //   else {
  //     setmessageData({ ...messageData, timezone: dayjs.tz.guess() });
  //   }
  // }, []);
  const getTimezoneLabel = () => {
    const obj = timezones.find(item => item.value === messageData.timezone);
    if (obj) {
      return obj.label;
    }
    return messageData.timezone;
  };
  // const timeZoneLabel = useMemo(() => getTimezoneLabel(), messageData.timezone);
  const timeZoneLabel = messageData.timezone;

  // The picked slot as the VIEWER's own clock reads it. `messageData.date`
  // already carries the wall clock chosen in the schedule's zone. Null — and
  // the line hides — when both zones agree.
  const viewerEquivalent = useMemo(
    () => viewerZoneEquivalent(messageData?.date, timeZone),
    [messageData?.date, timeZone]
  );

  useEffect(() => {
    if (mode === "EDIT" && editMessageData?.taskSendDatetime) {
      // Seeded in the SCHEDULE's zone — the zone this popup sends with and the
      // one the working-hours limits are checked against — so the time shown is
      // the time that will be saved.
      const dateValue = dayjs(
        formatDate(
          editMessageData?.taskSendDatetime,
          DATE_TIME_FORMATS.YYYY_MM_DD_T_HH_MM_SS,
          timeZone
        )
      ).toISOString();
      setmessageData({
        message: editMessageData?.message,
        date: dateValue,
        time: dateValue,
        timezone: timeZone,
      });
    } else if (mode !== "EDIT") {
      setmessageData({
        message: editMessageData?.message,
        date: null,
        timezone: timeZone,
      });
    }
  }, []);
  const handleSave = async () => {
    try {
      if (messageData.message === "") {
        return message.error("Please enter the message you would like to send in the future");
      }
      if (mode === "EDIT") {
        const variables = {
          id: editMessageData?.id,
          scheduledDate: dayjs(messageData?.date).format("YYYY-MM-DDTHH:mm:ss"),
          timezone: timeZone,
          message: messageData?.message,
        };
        const resp = await updateScheduledMessage({
          variables,
        });
        if (resp?.data?.updateScheduledMessage?.statusCode === 200) {
          message.success("Scheduled message updated successfully");
          const index = scheduledMessages.findIndex(item => item?.id === editMessageData?.id);
          if (index > -1) {
            dispatch(
              updateScheduledMessageRedux({
                index,
                message: resp?.data?.updateScheduledMessage?.scheduledMessage,
              })
            );
          }
          refetchScheduledMessagesList();
          setmessageText("");
          reset();
          close();
          // Refetch messages from page 1 to show the updated scheduled message
          getMessages({
            variables: {
              leadId: selectedLead?.id,
              page: 1,
            },
          });
        } else {
          console.error(resp?.data?.updateScheduledMessage?.message);
          message.error("Message failed to schedule. Please try again or contact support.");
        }
      } else {
        const variables = {
          leadId: selectedLead?.id,
          userId: user?.id,
          scheduledDate: dayjs(messageData?.date).format("YYYY-MM-DDTHH:mm:ss"),
          timezone: timeZone,
          message: messageData?.message,
          // Mirrors CREATE_MESSAGE: explicit thread opp when one is chosen,
          // otherwise the active opp-type tab.
          leadOpportunityId: resolveLeadOpportunityId(messageOpportunity),
          selectedOppTypeId: selectedOppTypeId || null,
        };
        const resp = await createScheduledMessage({
          variables,
        });

        if (resp?.data?.createScheduledMessage?.statusCode === 200) {
          setmessageText("");
          reset();
          message.success(resp?.data?.createScheduledMessage?.message);
          dispatch(addScheduledMessage(resp.data.createScheduledMessage.scheduledMessage));
          refetchScheduledMessagesList();
          close();
          // Refetch messages from page 1 to show the new scheduled message
          getMessages({
            variables: {
              leadId: selectedLead?.id,
              page: 1,
            },
          });
        } else {
          message.error("Message failed to schedule. Please try again or contact support.");
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (mode === "EDIT") {
      form.setFieldsValue({
        message: messageData?.message || "",
        date: dayjs(messageData.date),
        time: dayjs(messageData.date),
      });
    } else {
      form.setFieldsValue({
        message: messageData?.message || "",
      });
    }
  }, [mode, messageData]);
  // Only working, future days — and only hours the location is open. Shared
  // with the email scheduler so both offer the same slots.
  const disabledDate = getWorkingHoursDisabledDate(workingHours, timeZone);
  // Scoped to the DATE the user picked, not the time picker's own value (which
  // is null until they choose one, and would otherwise resolve to today).
  const disabledTime = () => getWorkingHoursDisabledTime(workingHours, messageData.date, timeZone);
  return (
    <div>
      <Form form={form} onFinish={() => handleSave()} layout="vertical">
        {mode !== "ADD" && (
          <Form.Item
            label="Message"
            name="message"
            rules={[{ required: true, message: "Please enter the message you want to schedule" }]}
          >
            <Input.TextArea
              className="rounded-[2px]"
              name="message"
              rows={4}
              placeholder="Enter the message you want to schedule"
              value={messageData?.message}
              defaultValue={messageData?.message}
              onChange={e => setmessageData(prev => ({ ...prev, message: e.target.value }))}
            />
          </Form.Item>
        )}
        <p className="mt-2 mb-1">Date and time you want the message to be delivered</p>
        <div className="flex-row-centered gap-1">
          <Form.Item
            className="w-[60%]"
            label="Due Date"
            rules={[{ required: true, message: "Please select date" }]}
            name="date"
          >
            <DatePicker
              className="w-full"
              name="date"
              onChange={value => {
                if (value)
                  setmessageData(prev => ({ ...prev, date: value.toISOString(), rawDate: value }));
              }}
              format={DATE_TIME_FORMATS.MM_DD_YYYY}
              disabledDate={disabledDate}
            />
          </Form.Item>
          <Form.Item
            label="Due Time"
            className="w-[40%]"
            rules={[{ required: true, message: "Please select time" }]}
            name="time"
          >
            <TimePicker
              name="time"
              className="w-full"
              format={DATE_TIME_FORMATS.HH_MM_A}
              use12Hours
              disabled={!messageData.date}
              disabledTime={disabledTime}
              showNow={false}
              // value={dayjs(messageData.date)}
              onChange={value => {
                if (value && messageData.date) {
                  const updatedDate = dayjs(messageData.date)
                    .hour(dayjs(value).hour())
                    .minute(dayjs(value).minute());
                  setmessageData(prev => ({
                    ...prev,
                    date: updatedDate.toISOString(),
                  }));
                }
              }}
            />
          </Form.Item>
        </div>
        <p className="mt-1">
          Current timezone: <b>{timeZoneLabel}</b>
        </p>
        <span className="text-xs text-grey-700">
          The message will be delivered to the lead on{" "}
          <span className="font-[500]">
            {convertUtcToLocal(messageData.date, DATE_TIME_FORMATS.MMM_DD_YYYY_COMMA_hh_mm_A)}
          </span>
        </span>
        {/* Second reading for anyone whose own clock differs — the pickers work
            in the dealership's zone, so "11:00 PM" there is a very different
            hour wherever the user actually is. Hidden when the zones match.
            Kept last: it annotates the delivery time stated above. */}
        {viewerEquivalent && (
          <p className="m-0 text-xs text-grey-500">
            That is <b>{viewerEquivalent.time}</b> in your timezone ({viewerEquivalent.zone}).
          </p>
        )}
      </Form>
      <div className="flex-row-centered mt-4 justify-start gap-2">
        <Button
          type="primary"
          loading={updateLoading || createLoading}
          onClick={() => form.submit()}
        >
          {mode === "EDIT" ? "Re-schedule Message" : "Schedule Message"}
        </Button>{" "}
        <Button
          onClick={() => {
            close();
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ScheduleMessagesPopup;
